import type { RemoteChannel } from '@remote-ui/core'
import { createEndpoint, fromMessagePort } from '@remote-ui/rpc'
import type { SandboxEndpoint } from '../sandbox/endpoint'
import type { IAdapter } from '../adapters/interface'
import { deepFnUnserialize } from '../utils/serialize'
import { createChannel } from './createChannel'

type OnError = (error: Error) => void

type LoadComponent = (
	{
		componentId,
		moduleUrl,
		modulePath,
		importKey,
		onError,
	}: { componentId: string; moduleUrl: string; modulePath: string; importKey: string; onError: OnError },
	{ channel }: { channel: RemoteChannel }
) => Promise<{
	render: (props?: any) => void
	unMount: () => void
}>

type SerializableError = {
	name: string
	message: string
}

export interface ExternalComponentBridge {
	loadComponent: LoadComponent
	onHostPortalAppended: (id: string, channel: RemoteChannel) => void
}
const componentErrorCallbacks: Record<string, OnError> = {}

const registerErrorHandler = (compGuid: string, onError: OnError) => {
	componentErrorCallbacks[compGuid] = onError
}
const deleteErrorHandler = (compGuid: string) => {
	delete componentErrorCallbacks[compGuid]
}

const onRenderError = (compGuid: string, { name, message }: SerializableError) => {
	const error = new Error(message)
	error.name = name
	componentErrorCallbacks[compGuid](error)
}

const componentsCounters: Record<string, number> = {}
const getCompGuid = (compId: string) => {
	componentsCounters[compId] = componentsCounters[compId] || 0
	componentsCounters[compId]++

	return `${compId}-${componentsCounters[compId]}`
}
export const createExternalComponentBridge = ({ adapter }: { adapter: IAdapter }): ExternalComponentBridge => {
	const { hostPort, remotePort } = createChannel()
	const endpoint = createEndpoint<SandboxEndpoint>(fromMessagePort(hostPort))
	endpoint.expose({ onRenderError })

	adapter?.registerPort?.({ remotePort })

	const loadComponent: LoadComponent = async (
		{ componentId, moduleUrl, modulePath, importKey, onError },
		{ channel }
	) => {
		const compGuid = getCompGuid(componentId)
		registerErrorHandler(compGuid, onError)

		await endpoint.call.loadComponent(compGuid, moduleUrl, modulePath, importKey, channel)

		return {
			render: async (props) => {
				const decoratedProps = deepFnUnserialize(props)

				await endpoint.call.render(compGuid, decoratedProps)
			},
			unMount: async () => {
				deleteErrorHandler(compGuid)
				await endpoint.call.unMount(compGuid)
			},
		}
	}

	const onHostPortalAppended = async (id: string, channel: RemoteChannel) => {
		await endpoint.call.onHostPortalAppended(id, channel)
	}

	return {
		loadComponent,
		onHostPortalAppended,
	}
}
